import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';

interface Workspace {
  id: string;
  name: string;
  companyId: string;
  companyName: string;
}

interface WorkspaceState {
  activeWorkspaceId: string | null;
  activeCompanyId: string | null;
  activeDomainId: string | null;
  workspaces: Workspace[];
  setActiveWorkspace: (workspaceId: string) => void;
  setWorkspaces: (workspaces: Workspace[]) => void;
  getActiveWorkspace: () => Workspace | undefined;
  setActiveDomainId: (domainId: string | null) => void;
  // Progressive complexity helpers
  showWorkspaceSelector: () => boolean;
  // All-workspaces (company-level) view
  isAllWorkspacesView: () => boolean;
}

export const useWorkspaceStore = create<WorkspaceState>()(
  devtools(
    persist(
      (set, get) => ({
        activeWorkspaceId: null,
        activeCompanyId: null,
        activeDomainId: null,
        workspaces: [],
        setActiveWorkspace: (workspaceId: string) => {
          const workspace = get().workspaces.find(w => w.id === workspaceId);
          set({
            activeWorkspaceId: workspaceId,
            // For 'all' view, keep existing companyId (from first workspace)
            activeCompanyId: workspaceId === 'all'
              ? (get().activeCompanyId || get().workspaces[0]?.companyId || null)
              : (workspace?.companyId || null),
            // Reset domain when workspace changes
            activeDomainId: null,
          });
          // Sync to localStorage for the API client (skip for 'all' view)
          if (typeof window !== 'undefined' && workspaceId !== 'all') {
            localStorage.setItem('hf-app:workspace-id', workspaceId);
          }
        },
        setWorkspaces: (workspaces: Workspace[]) => {
          set({ workspaces });
          // Auto-select first workspace if none active
          if (!get().activeWorkspaceId && workspaces.length > 0) {
            get().setActiveWorkspace(workspaces[0].id);
          }
        },
        getActiveWorkspace: () => {
          const state = get();
          return state.workspaces.find(w => w.id === state.activeWorkspaceId);
        },
        setActiveDomainId: (domainId: string | null) => {
          set({ activeDomainId: domainId });
        },
        showWorkspaceSelector: () => get().workspaces.length > 1,
        isAllWorkspacesView: () => get().activeWorkspaceId === 'all',
      }),
      {
        name: 'hf-app:workspace',
        partialize: (state) => ({
          activeWorkspaceId: state.activeWorkspaceId,
          activeCompanyId: state.activeCompanyId,
        }),
      },
    ),
    { name: 'WorkspaceStore' },
  ),
);
